import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';
import {
  Mail,
  Lock,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
} from 'lucide-react';

interface ExameFlutuanteItem {
  tipo: string;
  tag: string;
  accentDot: string;
  accentText: string;
}

interface CenarioFlutuante {
  positionClass: string;
  layoutClass: string;
  items: ExameFlutuanteItem[];
}

const CENARIOS_FLUTUANTES: CenarioFlutuante[] = [
  // 1. Acima do Box (Centralizado seguro)
  {
    positionClass: '-top-14 sm:-top-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Exame Admissional',
        tag: 'NR-07 • Agilidade na Contratação',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Audiometria Ocupacional',
        tag: 'Exame Complementar PGR',
        accentDot: 'bg-teal-500',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 2. Acima à Direita (contido na coluna do form, sem vazar a janela à direita)
  {
    positionClass: '-top-12 sm:-top-14 right-0 sm:right-2 flex justify-end px-2',
    layoutClass: 'flex flex-col sm:flex-row items-end sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Exame Periódico',
        tag: 'Vigência Preventiva Anual',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Acuidade Visual',
        tag: 'Saúde Ocular Ocupacional',
        accentDot: 'bg-blue-500',
        accentText: 'text-blue-700',
      },
    ],
  },
  // 3. Abaixo do Box (Centralizado seguro)
  {
    positionClass: '-bottom-14 sm:-bottom-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Exame Demissional',
        tag: 'Homologação e Desligamento',
        accentDot: 'bg-blue-500',
        accentText: 'text-blue-700',
      },
      {
        tipo: 'Retorno ao Trabalho',
        tag: 'Aptidão Clínica Ocupacional',
        accentDot: 'bg-teal-500',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 4. Acima à Esquerda (seguro, na margem do próprio form, sem ir para a imagem à esquerda)
  {
    positionClass: '-top-12 sm:-top-14 left-0 sm:left-2 flex justify-start px-2',
    layoutClass: 'flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Mudança de Risco',
        tag: 'Atualização de GHE e Riscos PGR',
        accentDot: 'bg-amber-500',
        accentText: 'text-amber-700',
      },
      {
        tipo: 'Exame Clínico NR-07',
        tag: 'Avaliação Médica Ocupacional',
        accentDot: 'bg-emerald-600',
        accentText: 'text-emerald-700',
      },
    ],
  },
  // 5. Acima do Box (ASO Digital & Espirometria Respiratória)
  {
    positionClass: '-top-14 sm:-top-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'ASO Digital em Tempo Real',
        tag: 'Assinatura Eletrônica ICP-Brasil',
        accentDot: 'bg-emerald-600',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Espirometria Ocupacional',
        tag: 'Avaliação Respiratória NR-07',
        accentDot: 'bg-teal-600',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 6. Acima à Direita (Toxicologia & Imagem Raio-X OIT)
  {
    positionClass: '-top-12 sm:-top-14 right-0 sm:right-2 flex justify-end px-2',
    layoutClass: 'flex flex-col sm:flex-row items-end sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Toxicológico Ocupacional',
        tag: 'Laboratorial Credenciado CLT',
        accentDot: 'bg-purple-500',
        accentText: 'text-purple-700',
      },
      {
        tipo: 'Raio-X de Tórax OIT',
        tag: 'Padrão Internacional OIT',
        accentDot: 'bg-indigo-500',
        accentText: 'text-indigo-700',
      },
    ],
  },
  // 7. Abaixo do Box (Cardiovascular & Psicossocial NR-33/NR-35)
  {
    positionClass: '-bottom-14 sm:-bottom-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Eletrocardiograma (ECG)',
        tag: 'Saúde Cardiovascular do Colaborador',
        accentDot: 'bg-rose-500',
        accentText: 'text-rose-700',
      },
      {
        tipo: 'Psicossocial Ocupacional',
        tag: 'Aptidão Mental NR-33 e NR-35',
        accentDot: 'bg-cyan-600',
        accentText: 'text-cyan-700',
      },
    ],
  },
  // 8. Acima à Esquerda (eSocial & Rede Nacional Credenciada)
  {
    positionClass: '-top-12 sm:-top-14 left-0 sm:left-2 flex justify-start px-2',
    layoutClass: 'flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'eSocial S-2220 / S-2240',
        tag: 'Envio Automático e Seguro',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Rede Nacional SST',
        tag: '+3.500 Clínicas Credenciadas',
        accentDot: 'bg-teal-600',
        accentText: 'text-teal-700',
      },
    ],
  },
];

export default function Login() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'forgot'>(() => {
    return searchParams.get('mode') === 'forgot' ? 'forgot' : 'login';
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorMessage, setTwoFactorMessage] = useState('');

  // Estado da Recuperação de Senha (Card Face 2)
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // Efeito 1: Digitação do Título (Typing Effect balanceado)
  const fullText = 'Gestão inteligente de Saúde e Segurança do Trabalho';
  const [typedCharsCount, setTypedCharsCount] = useState(0);
  const [typingComplete, setTypingComplete] = useState(false);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      setTypedCharsCount(current);
      if (current >= fullText.length) {
        clearInterval(interval);
        setTypingComplete(true);
      }
    }, 45); // Cadência suave nem muito rápida nem muito lenta

    return () => clearInterval(interval);
  }, []);

  const typedText = useMemo(() => fullText.slice(0, typedCharsCount), [typedCharsCount]);

  // Efeito 2: Exames Flutuantes ao Redor do Card (Entrada 1 a 1 com intervalo de 2.4s, e Saída em Fumaça)
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [item1Visible, setItem1Visible] = useState(false);
  const [item2Visible, setItem2Visible] = useState(false);

  useEffect(() => {
    // 1. Imediato: Primeiro item materializa na fumaça
    setItem1Visible(true);
    setItem2Visible(false);

    // 2. Após 2.4s: Segundo item materializa na fumaça ao lado / abaixo
    const t1 = setTimeout(() => {
      setItem2Visible(true);
    }, 2400);

    // 3. Após mais 3.0s (t = 5.4s): Primeiro item se dissipa em fumaça
    const t2 = setTimeout(() => {
      setItem1Visible(false);
    }, 5400);

    // 4. Após mais 250ms (t = 5.65s): Segundo item se dissipa em fumaça
    const t3 = setTimeout(() => {
      setItem2Visible(false);
    }, 5650);

    // 5. Após a fumaça dissipar completamente (t = 6.6s): Avança para a próxima cena
    const t4 = setTimeout(() => {
      setCurrentSceneIndex((prev) => (prev + 1) % CENARIOS_FLUTUANTES.length);
    }, 6600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [currentSceneIndex]);

  const currentScene = CENARIOS_FLUTUANTES[currentSceneIndex];

  // Efeito 3: Apresentação da Caixinha de Login (Primeiro Boas-Vindas, depois o Formulário)
  const [showWelcome, setShowWelcome] = useState(false);
  const [showFormBox, setShowFormBox] = useState(false);

  useEffect(() => {
    const welcomeTimer = setTimeout(() => setShowWelcome(true), 80);
    const formTimer = setTimeout(() => setShowFormBox(true), 480);

    return () => {
      clearTimeout(welcomeTimer);
      clearTimeout(formTimer);
    };
  }, []);

  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();

  const handleOpenForgot = () => {
    setForgotEmail(email);
    setForgotSuccess(false);
    setForgotError('');
    setMode('forgot');
  };

  const handleBackToLogin = () => {
    if (forgotEmail) {
      setEmail(forgotEmail);
    }
    setForgotSuccess(false);
    setForgotError('');
    setMode('login');
  };

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!email || (!requires2FA && !password) || (requires2FA && !twoFactorCode)) {
      setError('Preencha os campos para continuar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (requires2FA) {
        const result = await verifyTwoFactor(email, twoFactorCode);
        if (result.success) {
          navigate('/');
        } else if (result.requiresPasswordChange) {
          navigate(`/change-password?email=${encodeURIComponent(email)}`);
        } else {
          setError(result.error || 'Código inválido ou expirado');
        }
      } else {
        const result = await login(email, password);
        if (result.success) {
          navigate('/');
        } else if (result.requiresPasswordChange) {
          navigate(`/change-password?email=${encodeURIComponent(email)}`);
        } else if (result.requires2FA) {
          setRequires2FA(true);
          setTwoFactorMessage(result.message || 'Código de verificação enviado para o seu email.');
        } else {
          setError(result.error || 'Erro ao fazer login');
        }
      }
    } catch {
      setError('Erro inesperado ao fazer login');
    } finally {
      setLoading(false);
    }
  }, [email, password, requires2FA, twoFactorCode, login, verifyTwoFactor, navigate]);

  const handleForgotSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const targetEmail = forgotEmail.trim() || email.trim();
    if (!targetEmail || !targetEmail.includes('@')) {
      setForgotError('Por favor, informe um email válido');
      return;
    }

    setForgotLoading(true);
    setForgotError('');
    setForgotSuccess(false);

    try {
      await authService.forgotPassword(targetEmail);
      setForgotSuccess(true);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      setForgotError(
        errorObj.response?.data?.message ||
        errorObj?.message ||
        'Erro ao solicitar recuperação de senha. Tente novamente.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen flex relative bg-[#042614] lg:bg-white overflow-hidden">
      {/* Background de Imagem para Mobile */}
      <div className="absolute inset-0 z-0 lg:hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#042614]/85 mix-blend-multiply z-10" />
        <img 
          src="/mapa_brasil.webp" 
          alt="SST Exames" 
          className="absolute inset-0 w-full h-full object-cover opacity-50 mix-blend-overlay"
        />
      </div>

      {/* Lado Esquerdo - Painel Visual Imersivo (Oculto em mobile, 50% em desktop) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#042614] overflow-hidden shadow-2xl z-10 flex-shrink-0">
        {/* Camadas de cor e textura */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#021f10]/90 via-[#05331a]/80 to-[#02180c]/95 z-10" />
        
        {/* Imagem do Mapa do Brasil com blend sofisticado */}
        <img 
          src="/mapa_brasil.webp" 
          alt="SST Exames Corporativo" 
          className="absolute inset-0 w-full h-full object-cover opacity-45 mix-blend-luminosity scale-105"
        />

        {/* Linhas Topográficas e de Fluxo de Dados Animadas */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-15 opacity-75"
          viewBox="0 0 900 1000"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="streamGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#34d399" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#059669" stopOpacity="0.2" />
            </linearGradient>
            <linearGradient id="streamGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#059669" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#6ee7b7" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#047857" stopOpacity="0.2" />
            </linearGradient>
            <radialGradient id="nodePulseGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#059669" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Curva Principal de Fluxo 1 */}
          <path
            d="M 500 60 C 440 200, 680 320, 580 470 C 490 610, 660 760, 540 940"
            stroke="url(#streamGrad1)"
            strokeWidth="2.5"
            strokeDasharray="140 220"
            className="animate-flow-dash"
            strokeLinecap="round"
          />

          {/* Curva Secundária Mais Rápida 2 */}
          <path
            d="M 680 120 C 590 280, 730 400, 630 570 C 550 700, 710 830, 610 970"
            stroke="url(#streamGrad2)"
            strokeWidth="1.8"
            strokeDasharray="90 180"
            className="animate-flow-dash-fast"
            strokeLinecap="round"
          />

          {/* Linha Sinusoidal Suave 3 */}
          <path
            d="M 430 160 C 370 310, 520 450, 460 590 C 410 730, 540 860, 470 990"
            stroke="url(#streamGrad1)"
            strokeWidth="1.5"
            strokeDasharray="180 300"
            className="animate-flow-dash-slow"
            strokeLinecap="round"
            opacity="0.6"
          />

          {/* Conexão Transversal */}
          <path
            d="M 370 510 C 490 470, 610 530, 760 480"
            stroke="url(#streamGrad2)"
            strokeWidth="1.5"
            strokeDasharray="80 140"
            className="animate-flow-dash-reverse"
            strokeLinecap="round"
            opacity="0.5"
          />

          {/* Nós Conectados no Mapa */}
          <g transform="translate(610, 680)">
            <circle r="14" fill="url(#nodePulseGlow)" className="animate-ping opacity-60" />
            <circle r="4" fill="#34d399" />
            <circle r="7" stroke="#10b981" strokeWidth="1" opacity="0.8" />
          </g>

          <g transform="translate(540, 480)">
            <circle r="11" fill="url(#nodePulseGlow)" className="animate-ping opacity-50" />
            <circle r="3.5" fill="#6ee7b7" />
            <circle r="6" stroke="#34d399" strokeWidth="1" opacity="0.7" />
          </g>

          <g transform="translate(710, 410)">
            <circle r="11" fill="url(#nodePulseGlow)" className="animate-ping opacity-50" />
            <circle r="3.5" fill="#34d399" />
            <circle r="6" stroke="#10b981" strokeWidth="1" opacity="0.6" />
          </g>

          <g transform="translate(520, 820)">
            <circle r="9" fill="url(#nodePulseGlow)" className="animate-ping opacity-40" />
            <circle r="3" fill="#34d399" />
          </g>
        </svg>

        {/* Conteúdo sobre a imagem: Logo e Título com Typing */}
        <div className="relative z-20 flex flex-col justify-center px-12 xl:px-20 text-white h-full w-full max-w-2xl">
          <img 
            src="/sst_logo_negativo.svg" 
            alt="SST Exames" 
            className="h-16 w-auto object-contain mb-10 origin-left transform scale-100 self-start drop-shadow-md" 
          />
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-[1.18] tracking-tight min-h-[96px] sm:min-h-[120px] lg:min-h-[140px]">
            <span className="text-emerald-400 drop-shadow-sm">
              {typedText.slice(0, 22)}
            </span>
            <span className="text-white">
              {typedText.slice(22)}
            </span>
            <span
              className={`inline-block w-1.5 lg:w-2 h-7 sm:h-9 lg:h-11 bg-emerald-400 ml-1.5 align-middle ${
                typingComplete ? 'animate-pulse' : 'opacity-100'
              }`}
            />
          </h1>

          <p className="text-sm text-emerald-100/70 max-w-lg leading-relaxed">
            Plataforma digital integrada de medicina ocupacional, agendamentos centralizados e laudos regulatórios para todo o Brasil.
          </p>
        </div>
      </div>

      {/* Lado Direito - Formulário com Flip 3D (Primeiro Bem-Vindo, depois Card) */}
      <div className="flex-1 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-12 xl:px-20 bg-transparent lg:bg-gray-50/80 relative z-20 h-full overflow-y-auto lg:overflow-visible">
        <div className="mx-auto w-full max-w-sm lg:max-w-md relative my-auto pt-10 pb-8">
          {/* Exames Flutuantes Livres ao redor do Card (Pílulas Glassmorphism Premium com Levitação Suave) */}
          <div
            className={`hidden lg:flex absolute z-30 pointer-events-none ${currentScene.positionClass}`}
          >
            <div className={currentScene.layoutClass}>
              {currentScene.items.map((item, idx) => {
                const isVisible = idx === 0 ? item1Visible : item2Visible;
                return (
                  <div
                    key={item.tipo}
                    className={`${isVisible ? 'smoke-appear' : 'smoke-dissolve'}`}
                  >
                    <div
                      className={`${
                        idx === 1 ? 'animate-float-flow-2' : 'animate-float-flow-1'
                      } flex items-center gap-2.5 sm:gap-3 select-none`}
                    >
                      <div className="relative flex items-center justify-center w-2.5 h-2.5 shrink-0">
                        <span className={`animate-pulse-glow absolute inline-flex h-full w-full rounded-full ${item.accentDot} opacity-60`} />
                        <span className={`relative inline-flex rounded-full h-2 w-2 ${item.accentDot}`} />
                      </div>
                      <div className="flex flex-col text-left">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs sm:text-[13px] font-bold text-gray-800 tracking-tight whitespace-nowrap">
                            {item.tipo}
                          </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold text-emerald-800 bg-emerald-50/90 rounded-full">
                            SST
                          </span>
                        </div>
                        <span className={`text-[10px] sm:text-[11px] ${item.accentText} font-semibold tracking-tight whitespace-nowrap`}>
                          {item.tag}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Logo para mobile */}
          <div className="lg:hidden mb-6 flex justify-center drop-shadow-lg">
            <img src="/sst_logo_negativo.svg" alt="SST Exames" className="h-12 w-auto" />
          </div>

          {/* 1. Primeiro: Cabeçalho com transição suave */}
          <div
            className={`transition-all duration-700 ease-out transform mb-6 text-center ${
              showWelcome
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 -translate-y-4'
            }`}
          >
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white lg:text-gray-900 transition-all duration-300">
              {mode === 'forgot'
                ? 'Recuperação de Senha'
                : requires2FA
                ? 'Código de Segurança'
                : 'Bem-vindo de volta'}
            </h2>
            <p className="mt-2 text-xs sm:text-sm font-medium text-emerald-100/90 lg:text-gray-500 max-w-xs mx-auto transition-all duration-300">
              {mode === 'forgot'
                ? 'Informe seu email corporativo para enviarmos as instruções de redefinição.'
                : requires2FA
                ? 'Digite o código de 6 dígitos enviado para seu email.'
                : 'Acesse o Portal do Cliente para gerenciar seus agendamentos, colaboradores e conformidade SST.'}
            </p>
          </div>

          {/* 2. Container com Flip 3D */}
          <div
            className={`transition-all duration-700 ease-out transform ${
              showFormBox
                ? 'opacity-100 translate-y-0 scale-100'
                : 'opacity-0 translate-y-8 scale-[0.98] pointer-events-none'
            }`}
          >
            <div className="perspective-1000 w-full">
              <div
                className={`w-full transition-transform duration-500 ease-out transform-style-3d relative ${
                  mode === 'forgot' ? 'rotate-y-180' : ''
                }`}
              >
                {/* Face Frontal: Login & 2FA */}
                <div
                  className={`w-full bg-white p-6 sm:p-7 shadow-2xl shadow-emerald-950/15 lg:shadow-xl lg:shadow-emerald-950/10 rounded-2xl border border-gray-100/80 backface-hidden overflow-hidden transition-all duration-300 ease-in-out ${
                    mode === 'forgot' ? 'absolute top-0 left-0 w-full pointer-events-none opacity-0' : 'relative z-10 opacity-100'
                  }`}
                >
                  {/* Linha de acento no topo do Card */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600 rounded-t-2xl" />

                  <form
                    className="space-y-3.5"
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label htmlFor="email" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                          <span>Email Corporativo</span>
                        </label>
                      </div>
                      <div className="relative group">
                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-600 transition-colors pointer-events-none">
                          <Mail className="w-4 h-4" />
                        </div>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && email && password) {
                              e.preventDefault();
                              handleSubmit();
                            }
                          }}
                          className="w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm bg-gray-50/70 hover:bg-gray-50/90 border border-gray-200/90 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600 transition-all text-gray-900 placeholder-gray-400 shadow-xs font-medium"
                          placeholder="seu@email.com"
                        />
                      </div>
                    </div>

                    {!requires2FA ? (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label htmlFor="password" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span>Senha de Acesso</span>
                          </label>
                          <button
                            type="button"
                            onClick={handleOpenForgot}
                            className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 hover:underline underline-offset-4 transition-all cursor-pointer"
                          >
                            Esqueci a senha
                          </button>
                        </div>
                        <div className="relative group">
                          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-600 transition-colors pointer-events-none">
                            <Lock className="w-4 h-4" />
                          </div>
                          <input
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && email && password) {
                                e.preventDefault();
                                handleSubmit();
                              }
                            }}
                            className="w-full pl-10 pr-11 py-2.5 sm:py-3 text-sm bg-gray-50/70 hover:bg-gray-50/90 border border-gray-200/90 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600 transition-all text-gray-900 placeholder-gray-400 shadow-xs font-medium"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors cursor-pointer rounded-lg hover:bg-gray-100"
                            title={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label htmlFor="twoFactorCode" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-700 mb-2 justify-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          <span>Código de Verificação (2FA)</span>
                        </label>
                        <div className="relative">
                          <input
                            id="twoFactorCode"
                            name="twoFactorCode"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            required
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && twoFactorCode.length === 6) {
                                e.preventDefault();
                                handleSubmit();
                              }
                            }}
                            className="w-full py-3 px-4 text-center text-2xl font-mono tracking-[0.35em] font-bold border-2 border-emerald-500/40 rounded-xl focus:outline-none focus:ring-4 focus:ring-emerald-500/15 focus:border-emerald-600 bg-emerald-50/40 text-gray-900 shadow-inner"
                            placeholder="000000"
                            autoFocus
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500 text-center">
                          Digite o código de 6 dígitos enviado para seu email
                        </p>
                      </div>
                    )}

                    {twoFactorMessage && (
                      <div className="bg-emerald-50/90 border border-emerald-200/80 rounded-xl p-3 shadow-xs animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="text-xs font-medium text-emerald-800">{twoFactorMessage}</div>
                      </div>
                    )}

                    {error && (
                      <div className="bg-red-50/90 border border-red-200/80 rounded-xl p-3 shadow-xs animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="text-xs font-medium text-red-700">{error}</div>
                      </div>
                    )}

                    <div className="pt-1">
                      <button
                        type="submit"
                        disabled={loading || !email || (!requires2FA && !password) || (requires2FA && twoFactorCode.length !== 6)}
                        className="group w-full py-3 sm:py-3.5 px-5 rounded-xl text-white font-bold text-sm bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-emerald-600 disabled:hover:to-teal-600 shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {loading ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>{requires2FA ? 'Verificando código...' : 'Acessando plataforma...'}</span>
                          </>
                        ) : (
                          <>
                            <span>{requires2FA ? 'Confirmar Código' : 'Entrar no Portal'}</span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Face Traseira: Recuperação de Senha (Esqueci a Senha) */}
                <div
                  className={`w-full bg-white p-6 sm:p-7 shadow-2xl shadow-emerald-950/15 lg:shadow-xl lg:shadow-emerald-950/10 rounded-2xl border border-gray-100/80 backface-hidden rotate-y-180 overflow-hidden transition-all duration-300 ease-in-out ${
                    mode === 'forgot' ? 'relative z-10 opacity-100' : 'absolute top-0 left-0 w-full pointer-events-none opacity-0'
                  }`}
                >
                  {/* Linha de acento no topo do Card */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600 rounded-t-2xl" />

                  {forgotSuccess ? (
                    <div className="space-y-4 py-1">
                      <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-sm">
                        <CheckCircle2 className="w-7 h-7" />
                      </div>
                      <div className="text-center space-y-1.5">
                        <h3 className="text-base font-bold text-gray-900">
                          Instruções enviadas!
                        </h3>
                        <p className="text-xs text-gray-600 leading-relaxed max-w-xs mx-auto">
                          Se o email <strong>{forgotEmail}</strong> estiver cadastrado na plataforma, enviamos o link com as instruções para redefinir sua senha.
                        </p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                        <p className="text-[11px] text-emerald-800">
                          Por favor, verifique também sua caixa de spam ou lixo eletrônico.
                        </p>
                      </div>
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={handleBackToLogin}
                          className="group w-full py-3 sm:py-3.5 px-5 rounded-xl text-white font-bold text-sm bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.99] shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                          <span>Voltar para o login</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form
                      className="space-y-3.5"
                      onSubmit={handleForgotSubmit}
                      noValidate
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label htmlFor="forgot-email" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            <span>Email Corporativo</span>
                          </label>
                        </div>
                        <div className="relative group">
                          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-600 transition-colors pointer-events-none">
                            <Mail className="w-4 h-4" />
                          </div>
                          <input
                            id="forgot-email"
                            name="forgot-email"
                            type="email"
                            required
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleForgotSubmit();
                              }
                            }}
                            className="w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm bg-gray-50/70 hover:bg-gray-50/90 border border-gray-200/90 rounded-xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-600 transition-all text-gray-900 placeholder-gray-400 shadow-xs font-medium"
                            placeholder="seu@email.com"
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500">
                          Enviaremos um link seguro para a redefinição de acesso.
                        </p>
                      </div>

                      {forgotError && (
                        <div className="bg-red-50/90 border border-red-200/80 rounded-xl p-3 shadow-xs animate-in fade-in slide-in-from-top-1 duration-200">
                          <div className="text-xs font-medium text-red-700">{forgotError}</div>
                        </div>
                      )}

                      <div className="pt-1 space-y-2">
                        <button
                          type="submit"
                          disabled={forgotLoading || !forgotEmail}
                          className="group w-full py-3 sm:py-3.5 px-5 rounded-xl text-white font-bold text-sm bg-gradient-to-r from-emerald-600 via-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-emerald-600 disabled:hover:to-teal-600 shadow-lg shadow-emerald-600/25 hover:shadow-xl hover:shadow-emerald-600/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          {forgotLoading ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Enviando instruções...</span>
                            </>
                          ) : (
                            <>
                              <span>Enviar instruções</span>
                              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={handleBackToLogin}
                          className="w-full py-2 px-4 rounded-xl text-xs font-bold text-gray-600 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                          <span>Voltar para o login</span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

